import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignIn } from '@clerk/clerk-react';
import { KeyRound, ShieldAlert } from 'lucide-react';
import { AuthCardLayout } from './layout/AuthCardLayout';
import { AuthErrorBanner } from './layout/AuthErrorBanner';
import { ForgotPasswordRequestForm } from './forms/ForgotPasswordRequestForm';
import { ForgotPasswordResetForm } from './forms/ForgotPasswordResetForm';

export const ForgotPasswordPage: React.FC = () => {
  const { isLoaded, signIn, setActive } = useSignIn();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSendResetCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;
    setIsLoading(true);
    setError(null);

    try {
      await signIn.create({ strategy: 'reset_password_email_code', identifier: email.trim() });
      setIsCodeSent(true);
    } catch (err: any) {
      setError(err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message || err?.message || 'Could not send reset code.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !signIn) return;
    setIsLoading(true);
    setError(null);

    try {
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code: code.trim(),
        password: newPassword,
      });

      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        navigate('/', { replace: true });
      } else {
        setError(`Password reset status: ${result.status}. Additional steps required.`);
      }
    } catch (err: any) {
      setError(err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message || err?.message || 'Failed to reset password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthCardLayout
      title={isCodeSent ? 'Reset Your Password' : 'Forgot Password?'}
      subtitle={isCodeSent ? `Enter the code sent to ${email} and choose a new password` : 'Enter your registered email address to receive a reset code'}
      icon={isCodeSent ? <ShieldAlert className="w-5 h-5 text-indigo-400" /> : <KeyRound className="w-5 h-5" />}
      headerLinkPrompt="Remember your password?"
      headerLinkAction="Sign in"
      headerLinkHref="/sign-in"
    >
      <AuthErrorBanner error={error} />
      {!isCodeSent ? (
        <ForgotPasswordRequestForm
          email={email}
          setEmail={setEmail}
          isLoading={!isLoaded || isLoading}
          onSubmit={handleSendResetCode}
        />
      ) : (
        <ForgotPasswordResetForm
          code={code}
          setCode={setCode}
          newPassword={newPassword}
          setNewPassword={setNewPassword}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          isLoading={!isLoaded || isLoading}
          onSubmit={handleResetPassword}
          onBackToEmail={() => setIsCodeSent(false)}
        />
      )}
    </AuthCardLayout>
  );
};

export default ForgotPasswordPage;
